import { type FC, useEffect, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useMountedState } from 'react-use'
import { CheckCircleIcon } from '@heroicons/react/20/solid'
import {
  accountRecovery,
  usePoll,
  AccountRecoveryStatus,
  useAccountRecovery,
  type AccountRecoveryOutput,
  AccountVerificationType,
  AccountRecoveryErrorType,
  accountRecoveryV2,
  useAccountRecoveryV2,
} from '@egw/api'
import { Button, Alert, AlertType, IconConfirmationCircle } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { WEALTH_ROUTES } from '@egw/constants'
import { useRouter } from '@egw/router'
import { sendEvent } from '@egw/events'
import { getIncodeClient } from '@egw/risk-service'
import { Layout, MobileRedirect, PageHeader } from '../../../components'
import { CameraPermissionState, ChannelType, type RecoveryFlow } from '../../../models'
import { getRecoveryFlowData, updateRecoveryFlowData } from '../../../services'
import { FrontId } from '../../../components/front-id/front-id'
import { BackId } from '../../../components/back-id/back-id'
import { ProcessId } from '../../../components/process-id/process-id'
import { Selfie } from '../../../components/selfie/selfie'
import { FaceMatch } from '../../../components/face-match/face-match'
import { RetrySteps } from '../../../components/retry-steps/retry-steps'
import '../../../../styles.css'
import { usePermissions } from '../../../hooks/doc-v'
import { ResetPermissions } from '../../../components/reset-permissions/reset-permissions'
import { ConsentScreen } from '../../../components/consent/consent'

export const RecoveryVerificationPage: FC = () => {
  const [showDocVIntro, setShowDocVIntro] = useState(false)
  const [flowData, setFlowData] = useState<RecoveryFlow>()

  const isMounted = useMountedState()
  const { t } = useTranslation('features-auth')
  const { navigate, searchParams } = useRouter()

  const verificationToken = searchParams().get('token')

  const { start, data, error, isLoading, isSuccess } = usePoll(
    () => accountRecovery({}),
    (d) =>
      (d.__typename === 'AccountRecoveryResponse' && d.status !== AccountRecoveryStatus.PENDING_DOCV_RESULT) ||
      !isMounted(),
    1000,
    60 * 5,
  )

  useEffect(() => {
    if (data?.__typename === 'AccountRecoveryResponse' && data.status === AccountRecoveryStatus.IN_REVIEW) {
      navigate(WEALTH_ROUTES.RECOVERY_REVIEW.path)
    }
    // User has successfully verified their identity via DocV
    if (isSuccess) {
      sendEvent('eg_docV_success', {
        eventSource: 'egWealth',
        category: 'passkey-recovery',
        subCategory: 'docV',
      })
    }
    if (data?.__typename === 'AccountRecoveryError') {
      sendEvent('eg_docV_fail', {
        eventSource: 'egWealth',
        category: 'passkey-recovery',
        subCategory: 'docV',
        failureReason: data.errorType,
      })
    }
    if (error) {
      sendEvent('eg_docV_fail', {
        eventSource: 'egWealth',
        category: 'passkey-recovery',
        subCategory: 'docV',
      })
    }
  }, [isSuccess, error, data])

  const {
    mutate: verifyEmailToken,
    isLoading: isEmailVerifying,
    error: emailError,
    isSuccess: isEmailVerified,
  } = useAccountRecovery({
    onSuccess: (res: AccountRecoveryOutput) => {
      if (res.__typename === 'AccountRecoveryResponse') {
        setFlowData(
          updateRecoveryFlowData((draft) => {
            draft.isEmailVerified = true
            draft.documentVerificationUrl = res.documentVerificationUrl
          }),
        )
      }
      if (res.__typename === 'AccountRecoveryError' && res.errorType === AccountRecoveryErrorType.EXPIRED) {
        navigate(`${WEALTH_ROUTES.SIGN_IN.path}?recoveryExpired=true`)
      }
    },
  })

  useEffect(() => {
    const onLoad = (): undefined => {
      const d = getRecoveryFlowData()
      if (d) {
        setFlowData(d)
        if (d.channel === ChannelType.EMAIL && verificationToken) {
          // SUCCESS FOR EMAIL MAGIC LINK
          // PHONE SUCCESS IS HANDLED ON OTP PAGE
          sendEvent('eg_verification_success', {
            eventSource: 'egWealth',
            category: 'passkey-recovery',
            subCategory: 'docV',
            verificationType: 'email',
          })
          void verifyEmailToken({ verifyType: AccountVerificationType.EMAIL_MAGIC_LINK, verificationToken })
          return
        }
        if (d.channel === ChannelType.PHONE && d.documentVerificationUrl) {
          setShowDocVIntro(true)
          return
        }
      }
      navigate(WEALTH_ROUTES.RECOVERY.path)
    }
    onLoad()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout>
      {flowData?.documentVerificationUrl && showDocVIntro && !isSuccess ? (
        <>
          <PageHeader title={t('recovery.verification_title')} />
          <p className="text-md">{t('recovery.verification_intro')}</p>
          <p className="text-xl mt-4 font-semibold">{t('recovery.verification_how_it_works')}</p>
          <p className="text-md mt-4 font-semibold">{t('recovery.verification_photograph_id')}</p>
          <p className="text-md">{t('recovery.verification_instructions_id')}</p>
          <p className="text-md mt-4 font-semibold">{t('recovery.verification_selfie_title')}</p>
          <p className="text-md">{t('recovery.verification_selfie_description')}</p>
          <p className="text-xl my-4 font-semibold">{t('recovery.verification_safe_and_secure_title')}</p>
          <p className="text-md">{t('recovery.verification_safe_secure_descrioption')}</p>
          <div className="flex-1 md:hidden" />
          <Alert alertType={AlertType.Info} description={t('recovery.verification_button_disclaimer')} className="my-sm" />
          <Button
            testId="recoveryContinueButton_primary_btn"
            className="w-full md:w-min"
            onClick={() => {
              sendEvent('user_docV_click', {
                eventSource: 'egWealth',
                category: 'passkey-recovery',
                subCategory: 'docV',
              })
              start()
              typeof window !== 'undefined' && window.open(flowData.documentVerificationUrl, '_blank')
            }}
            isLoading={isLoading}
          >
            {t('recovery.continue')}
          </Button>
        </>
      ) : null}

      {isEmailVerifying ? (
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <Skeleton count={1} height="100px" className="mb-10" />
          <Skeleton count={5} height="50px" />
        </SkeletonTheme>
      ) : null}

      {flowData && flowData.channel === ChannelType.EMAIL && isEmailVerified && !showDocVIntro ? (
        <>
          <PageHeader title={t('recovery.email_magic_link.email_verification')} />
          <div className="flex items-center">
            <CheckCircleIcon className="mr-2 h-4 w-4 fill-green-400" />
            <span className="text-green-600">
              {t('recovery.email_magic_link.email_verified_message', {
                email: flowData.email,
              })}
            </span>
          </div>
          <p className="mt-4 mb-xl">{t('recovery.email_magic_link.email_verification_complete')}</p>
          <div className="flex-1 md:hidden" />
          <Button
            testId="emailVerifiedContinueButton_primary_btn"
            className="w-full md:w-max"
            onClick={() => {
              sendEvent('user_docV_click', {
                eventSource: 'egWealth',
                category: 'docV',
                subCategory: 'docV',
              })
              setShowDocVIntro(true)
            }}
          >
            {t('recovery.continue')}
          </Button>
        </>
      ) : null}

      {isSuccess ? (
        <>
          <PageHeader title={t('recovery.identity_verified')} />
          <IconConfirmationCircle className="mb-2xs" />
          <p className="mt-4 mb-6 md:mb-20 text-body">{t('recovery.identity_verified_message')}</p>
          <div className="flex-1 md:hidden" />
          <Button
            testId="identityVerifiedContinueButton_primary_btn"
            className="w-full md:w-max"
            onClick={() => {
              navigate(WEALTH_ROUTES.RECOVERY_PASSKEY.path)
            }}
          >
            {t('recovery.continue')}
          </Button>
        </>
      ) : null}
    </Layout>
  )
}

const saveDeviceData = (session: { token: string }): void => {
  void getIncodeClient().sendGeolocation({ token: session.token })
  void getIncodeClient().sendFingerprint({ token: session.token })
}

export const RecoveryVerificationPageV2: FC = () => {
  const [showDocVIntro, setShowDocVIntro] = useState(false)
  const [showDocV, setDocV] = useState(false)
  const [flowData, setFlowData] = useState<RecoveryFlow>()
  const [step, setStep] = useState(0)
  const permissionsState = usePermissions()
  const [resetPermissions, setResetPermissions] = useState(false)
  const [incodeSession, setIncodeSession] = useState<{ token: string } | undefined>(undefined)
  const [liveness, setLiveness] = useState(false)
  const [userExists, setUserExists] = useState(false)

  const isMounted = useMountedState()
  const { t } = useTranslation('features-auth')
  const { navigate, searchParams } = useRouter()
  const verificationToken = searchParams().get('token')

  const { start, data, error, isLoading, isSuccess } = usePoll(
    () => accountRecoveryV2({}),
    (d) =>
      (d.__typename === 'AccountRecoveryResponseV2' && d.status !== AccountRecoveryStatus.PENDING_DOCV_RESULT) ||
      !isMounted(),
    1000,
    60 * 5,
  )

  useEffect(() => {
    if (data?.__typename === 'AccountRecoveryResponseV2' && data.status === AccountRecoveryStatus.IN_REVIEW) {
      navigate(WEALTH_ROUTES.RECOVERY_REVIEW.path)
    }
    // User has successfully verified their identity via DocV
    if (isSuccess) {
      sendEvent('eg_docV_success', {
        eventSource: 'egWealth',
        category: 'passkey-recovery',
        subCategory: 'docV',
      })
    }
    if (data?.__typename === 'AccountRecoveryError') {
      sendEvent('eg_docV_fail', {
        eventSource: 'egWealth',
        category: 'passkey-recovery',
        subCategory: 'docV',
        failureReason: data.errorType,
      })
    }
    if (error) {
      sendEvent('eg_docV_fail', {
        eventSource: 'egWealth',
        category: 'passkey-recovery',
        subCategory: 'docV',
      })
    }
  }, [isSuccess, error, data])

  const {
    mutate: verifyEmailToken,
    isLoading: isEmailVerifying,
    error: emailError,
    isSuccess: isEmailVerified,
  } = useAccountRecoveryV2()

  useEffect(() => {
    const onLoad = async (): Promise<undefined> => {
      const d = getRecoveryFlowData()
      if (d) {
        setFlowData(d)
        if (d.channel === ChannelType.EMAIL && verificationToken) {
          // SUCCESS FOR EMAIL MAGIC LINK
          // PHONE SUCCESS IS HANDLED ON OTP PAGE
          sendEvent('eg_verification_success', {
            eventSource: 'egWealth',
            category: 'passkey-recovery',
            subCategory: 'docV',
            verificationType: 'email',
          })
          const res = await verifyEmailToken({
            verifyType: AccountVerificationType.EMAIL_MAGIC_LINK,
            verificationToken,
          })

          if (res?.__typename === 'AccountRecoveryResponseV2') {
            setFlowData(
              updateRecoveryFlowData((draft) => {
                draft.isEmailVerified = true
                draft.documentVerificationUrl = res.documentVerificationBaseUrl
                draft.verificationId = res.verificationId
              }),
            )
          }
          if (res?.__typename === 'AccountRecoveryError' && res.errorType === AccountRecoveryErrorType.EXPIRED) {
            navigate(`${WEALTH_ROUTES.SIGN_IN.path}?recoveryExpired=true`)
          }

          return
        }
        if (d.channel === ChannelType.PHONE) {
          setShowDocVIntro(true)
          return
        }
      }
      navigate(WEALTH_ROUTES.RECOVERY.path)
    }
    onLoad()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const goNext = (): void => {
    setStep(step + 1)
  }

  useEffect(() => {
    // if permissions are denied from start, let's show the reset permissions screen
    setResetPermissions(permissionsState === CameraPermissionState.DENIED)
  }, [permissionsState])

  const handleError = (e?: { type: string }): void => {
    if (e?.type === 'permissionDenied') {
      setResetPermissions(true)
    }
  }

  useEffect(() => {
    if (!flowData?.verificationId) {
      return
    }

    void getIncodeClient()
      .createSession('US', flowData.verificationId)
      .then(async (session) => {
        await getIncodeClient().warmup()
        setIncodeSession(session)

        saveDeviceData(session)
      })
  }, [flowData])

  if (resetPermissions) {
    return (
      <ResetPermissions
        onTryAgain={() => {
          setResetPermissions(false)
        }}
      />
    )
  }

  return (
    <Layout>
      {showDocVIntro && !isSuccess && !showDocV ? (
        <>
          <PageHeader title={t('recovery.verification_title')} />
          <p className="text-md">{t('recovery.verification_intro')}</p>
          <p className="text-xl mt-4 font-semibold">{t('recovery.verification_how_it_works')}</p>
          <p className="text-md mt-4 font-semibold">{t('recovery.verification_photograph_id')}</p>
          <p className="text-md">{t('recovery.verification_instructions_id')}</p>
          <p className="text-md mt-4 font-semibold">{t('recovery.verification_selfie_title')}</p>
          <p className="text-md">{t('recovery.verification_selfie_description')}</p>
          <p className="text-xl my-4 font-semibold">{t('recovery.verification_safe_and_secure_title')}</p>
          <p className="text-md">{t('recovery.verification_safe_secure_descrioption')}</p>
          <div className="flex-1 md:hidden" />
          <Button
            testId="recoveryContinueButton_primary_btn"
            className="w-full md:w-min mt-sm"
            onClick={() => {
              sendEvent('user_docV_click', {
                eventSource: 'egWealth',
                category: 'passkey-recovery',
                subCategory: 'docV',
              })
              setDocV(true)
            }}
            isLoading={isLoading}
          >
            {t('recovery.continue')}
          </Button>
        </>
      ) : null}

      {isEmailVerifying ? (
        <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
          <Skeleton count={1} height="100px" className="mb-10" />
          <Skeleton count={5} height="50px" />
        </SkeletonTheme>
      ) : null}

      {flowData && flowData.channel === ChannelType.EMAIL && isEmailVerified && !showDocVIntro ? (
        <>
          <PageHeader title={t('recovery.email_magic_link.email_verification')} />
          <div className="flex items-center">
            <CheckCircleIcon className="mr-2 h-4 w-4 fill-green-400" />
            <span className="text-green-600">
              {t('recovery.email_magic_link.email_verified_message', {
                email: flowData.email,
              })}
            </span>
          </div>
          <p className="mt-4 mb-xl">{t('recovery.email_magic_link.email_verification_complete')}</p>
          <div className="flex-1 md:hidden" />
          <Button
            testId="emailVerifiedContinueButton_primary_btn"
            className="w-full md:w-max"
            onClick={() => {
              sendEvent('user_docV_click', {
                eventSource: 'egWealth',
                category: 'docV',
                subCategory: 'docV',
              })
              setShowDocVIntro(true)
            }}
          >
            {t('recovery.continue')}
          </Button>
        </>
      ) : null}

      {showDocV && incodeSession ? (
        <>
          {
            [
              !getIncodeClient().isDesktop() && (
                <ConsentScreen key="consent" session={incodeSession} onAccept={goNext} />
              ),
              <MobileRedirect
                key={`mobile-redirect-${step.toString()}`}
                session={incodeSession}
                flowId={flowData?.verificationId ?? ''}
                onSuccess={() => {
                  if (getIncodeClient().isDesktop()) {
                    start()
                  } else {
                    goNext()
                  }
                }}
                onError={handleError}
              />,
              <FrontId key="front" session={incodeSession} onSuccess={goNext} onError={handleError} />,
              <BackId key="back" session={incodeSession} onSuccess={goNext} onError={handleError} />,
              <ProcessId key="process" session={incodeSession} onSuccess={goNext} />,
              <Selfie
                key="selfie"
                session={incodeSession}
                onSuccess={(res) => {
                  setLiveness(res?.liveness)
                  setUserExists(res?.existingUser)
                  goNext()
                }}
                onError={handleError}
              />,
              <FaceMatch
                key="facematch"
                session={incodeSession}
                onSuccess={() => {
                  start()
                  setDocV(false)
                }}
                liveness={liveness}
                userExists={userExists}
              />,
              <RetrySteps
                key="retry"
                session={incodeSession}
                numberOfTries={3}
                onSuccess={goNext}
                onError={handleError}
              />,
            ].filter(Boolean)[step]
          }
        </>
      ) : null}

      {isSuccess ? (
        <>
          <PageHeader title={t('recovery.identity_verified')} />
          <IconConfirmationCircle className="mb-2xs" />
          <p className="mt-4 mb-6 md:mb-20 text-body">{t('recovery.identity_verified_message')}</p>
          <div className="flex-1 md:hidden" />
          <Button
            testId="identityVerifiedContinueButton_primary_btn"
            className="w-full md:w-max"
            onClick={() => {
              navigate(WEALTH_ROUTES.RECOVERY_PASSKEY.path)
            }}
          >
            {t('recovery.continue')}
          </Button>
        </>
      ) : null}
    </Layout>
  )
}
